import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { 
  useMyTickets, 
  useMyTicketsRealtime,
  useTicketMessages, 
  useTicketMessagesRealtime,
  useCreateTicket, 
  useSendMessage,
  useUpdateTicketStatus,
  useMarkSolution,
  uploadTicketAttachment,
  type TicketRow,
} from '@/hooks/useTickets';
import { 
  Plus, 
  Ticket, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  MessageSquare,
  Search,
  Filter,
  Send,
  Headphones,
  Loader2,
  XCircle,
  Paperclip,
  X,
  Image as ImageIcon,
  FileText,
  Download,
  ShieldCheck,
} from 'lucide-react';

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
type TicketCategory = 'technical' | 'access' | 'content' | 'billing' | 'other';

interface LocalAttachment {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
}

const categoryLabels: Record<TicketCategory, string> = {
  technical: 'Problema Técnico',
  access: 'Acceso a Plataforma',
  content: 'Contenido',
  billing: 'Facturación',
  other: 'Otro'
};

const statusLabels: Record<string, string> = {
  open: 'Abierto',
  in_progress: 'En Proceso',
  resolved: 'Resuelto',
  closed: 'Cerrado'
};

const statusColors: Record<string, string> = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-muted text-muted-foreground border-border'
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'open': return <AlertCircle className="w-4 h-4" />;
    case 'in_progress': return <Loader2 className="w-4 h-4 animate-spin" />;
    case 'resolved': return <CheckCircle2 className="w-4 h-4" />;
    case 'closed': return <XCircle className="w-4 h-4" />;
    default: return <AlertCircle className="w-4 h-4" />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function SupportPage() {
  const { user } = useAuth();
  const { data: tickets = [], isLoading } = useMyTickets();
  useMyTicketsRealtime();
  const createTicket = useCreateTicket();
  const sendMessage = useSendMessage();
  const updateStatus = useUpdateTicketStatus();
  const markSolution = useMarkSolution();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TicketStatus | 'all'>('all');
  const [newMessage, setNewMessage] = useState('');
  const [messageAttachments, setMessageAttachments] = useState<LocalAttachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const messageFileInputRef = useRef<HTMLInputElement>(null);
  const ticketFileInputRef = useRef<HTMLInputElement>(null);

  const [newTicket, setNewTicket] = useState({ title: '', description: '', category: '' as TicketCategory | '' });
  const [newTicketAttachments, setNewTicketAttachments] = useState<LocalAttachment[]>([]);

  const selectedTicket = tickets.find(t => t.id === selectedTicketId) ?? null;
  const { data: messages = [] } = useTicketMessages(selectedTicketId);
  useTicketMessagesRealtime(selectedTicketId);

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    setAttachments: React.Dispatch<React.SetStateAction<LocalAttachment[]>>
  ) => {
    const files = event.target.files;
    if (!files) return;
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

    Array.from(files).forEach(file => {
      if (file.size > maxSize) {
        toast({ title: "Archivo muy grande", description: `${file.name} excede 10MB`, variant: "destructive" });
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        toast({ title: "Tipo no permitido", description: `${file.type} no permitido`, variant: "destructive" });
        return;
      }
      setAttachments(prev => [...prev, { id: crypto.randomUUID(), file, name: file.name, type: file.type, size: file.size }]);
    });
    event.target.value = '';
  };

  const removeAttachment = (id: string, setAttachments: React.Dispatch<React.SetStateAction<LocalAttachment[]>>) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const uploadAttachments = async (attachments: LocalAttachment[]) => {
    if (!user || attachments.length === 0) return [];
    const results = await Promise.all(
      attachments.map(a => uploadTicketAttachment(a.file, user.id))
    );
    return results;
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim() || !newTicket.category) {
      toast({ title: "Campos requeridos", description: "Completa todos los campos obligatorios.", variant: "destructive" });
      return;
    }
    setIsSending(true);
    try {
      const attachmentUrls = await uploadAttachments(newTicketAttachments);
      await createTicket.mutateAsync({
        title: newTicket.title,
        description: newTicket.description,
        category: newTicket.category,
        firstMessage: newTicket.description,
        attachmentUrls,
      });
      setNewTicket({ title: '', description: '', category: '' });
      setNewTicketAttachments([]);
      setIsCreateDialogOpen(false);
      toast({ title: "Ticket creado", description: "Tu solicitud ha sido enviada al equipo de soporte." });
    } catch (e: any) {
      console.error('[SupportPage] handleCreateTicket error:', e);
      toast({ title: "Error", description: e.message || 'Error desconocido al crear el ticket', variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && messageAttachments.length === 0) || !selectedTicketId) return;
    setIsSending(true);
    try {
      const attachmentUrls = await uploadAttachments(messageAttachments);
      await sendMessage.mutateAsync({
        ticketId: selectedTicketId,
        message: newMessage,
        senderType: 'user',
        attachmentUrls,
      });
      setNewMessage('');
      setMessageAttachments([]);
    } catch (e: any) {
      toast({ title: "Error al enviar", description: e.message, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (ticket.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Headphones className="w-8 h-8 text-primary" />
            Soporte Técnico
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona tus tickets y obtén asistencia del equipo de soporte
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              Nuevo Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Ticket</DialogTitle>
              <DialogDescription>
                Describe tu problema o consulta y nuestro equipo te ayudará.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Título *</label>
                <Input 
                  placeholder="Resumen breve del problema"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Categoría *</label>
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket(prev => ({ ...prev, category: v as TicketCategory }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Descripción *</label>
                <Textarea 
                  placeholder="Describe tu problema en detalle..."
                  rows={4}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Archivos adjuntos</label>
                <div className="flex items-center gap-2">
                  <input ref={ticketFileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => handleFileSelect(e, setNewTicketAttachments)} />
                  <Button type="button" variant="outline" size="sm" onClick={() => ticketFileInputRef.current?.click()} className="gap-2">
                    <Paperclip className="w-4 h-4" />
                    Adjuntar archivos
                  </Button>
                  <span className="text-xs text-muted-foreground">Máx. 10MB</span>
                </div>
                {newTicketAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {newTicketAttachments.map(a => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border">
                        {a.type.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
                        <div className="flex flex-col">
                          <span className="text-xs font-medium truncate max-w-[120px]">{a.name}</span>
                          <span className="text-xs text-muted-foreground">{formatFileSize(a.size)}</span>
                        </div>
                        <button onClick={() => removeAttachment(a.id, setNewTicketAttachments)} className="ml-1 p-1 hover:bg-destructive/20 rounded-full transition-colors">
                          <X className="w-3 h-3 text-destructive" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleCreateTicket} className="w-full gap-2" disabled={isSending}>
                {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar Ticket
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20"><Ticket className="w-5 h-5 text-primary" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.total}</p><p className="text-xs text-muted-foreground">Total Tickets</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><AlertCircle className="w-5 h-5 text-blue-400" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.open}</p><p className="text-xs text-muted-foreground">Abiertos</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20"><Clock className="w-5 h-5 text-yellow-400" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.inProgress}</p><p className="text-xs text-muted-foreground">En Proceso</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20"><CheckCircle2 className="w-5 h-5 text-green-400" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.resolved}</p><p className="text-xs text-muted-foreground">Resueltos</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar tickets..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as TicketStatus | 'all')}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(statusLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tickets List & Detail View */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {isLoading ? (
            <Card className="bg-card/50 border-border">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin mb-3" />
                <p className="text-muted-foreground">Cargando tickets...</p>
              </CardContent>
            </Card>
          ) : filteredTickets.length === 0 ? (
            <Card className="bg-card/50 border-border">
              <CardContent className="p-8 text-center">
                <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No hay tickets que mostrar</p>
              </CardContent>
            </Card>
          ) : (
            filteredTickets.map(ticket => (
              <Card 
                key={ticket.id}
                className={`bg-card/50 border cursor-pointer transition-all hover:border-primary/50 ${
                  selectedTicketId === ticket.id ? 'border-primary ring-1 ring-primary/20' : 'border-border'
                }`}
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-foreground line-clamp-1 flex-1">{ticket.title}</h3>
                    <Badge variant="outline" className={statusColors[ticket.status] ?? ''}>
                      <StatusIcon status={ticket.status} />
                      <span className="ml-1">{statusLabels[ticket.status] ?? ticket.status}</span>
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{ticket.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-xs">{categoryLabels[ticket.category as TicketCategory] ?? ticket.category}</Badge>
                    <span>{new Date(ticket.created_at).toLocaleDateString('es-ES')}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Ticket Detail */}
        <div className="lg:col-span-3">
          {selectedTicket ? (
            <Card className="bg-card/50 border-border h-full flex flex-col">
              <CardHeader className="border-b border-border">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{selectedTicket.title}</CardTitle>
                    <CardDescription className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={statusColors[selectedTicket.status] ?? ''}>
                        <StatusIcon status={selectedTicket.status} />
                        <span className="ml-1">{statusLabels[selectedTicket.status] ?? selectedTicket.status}</span>
                      </Badge>
                      <Badge variant="outline">{categoryLabels[selectedTicket.category as TicketCategory] ?? selectedTicket.category}</Badge>
                    </CardDescription>
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    <p>Creado: {new Date(selectedTicket.created_at).toLocaleDateString('es-ES')}</p>
                    <p>Actualizado: {new Date(selectedTicket.updated_at).toLocaleDateString('es-ES')}</p>
                  </div>
                </div>
              </CardHeader>
              
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map(msg => {
                    const isSolution = selectedTicket.solution_message_id === msg.id;
                    return (
                      <div key={msg.id}>
                        {isSolution && (
                          <div className="flex items-center gap-2 mb-1 ml-1">
                            <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs font-medium text-green-500">Solución</span>
                          </div>
                        )}
                        <div className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={cn(
                            "max-w-[80%] rounded-2xl px-4 py-3",
                            msg.sender_type === 'user'
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : 'bg-muted text-foreground rounded-bl-md',
                            isSolution && 'ring-2 ring-green-500/50 bg-green-500/10'
                          )}>
                            <p className="text-sm">{msg.message}</p>
                            {msg.attachments && Array.isArray(msg.attachments) && (msg.attachments as any[]).length > 0 && (
                              <div className="mt-2 space-y-2">
                                {(msg.attachments as any[]).map((att: any, idx: number) => (
                                  <div key={idx}>
                                    {att.type?.startsWith('image/') && (
                                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="block mb-1">
                                        <img
                                          src={att.url}
                                          alt={att.name}
                                          className="max-w-[240px] max-h-[180px] rounded-lg border border-border/50 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                          loading="lazy"
                                        />
                                      </a>
                                    )}
                                    <a
                                      href={att.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-2 text-xs p-2 rounded ${
                                        msg.sender_type === 'user' ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'bg-background hover:bg-background/80'
                                      } transition-colors`}
                                    >
                                      {att.type?.startsWith('image/') ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                      <span className="truncate max-w-[150px]">{att.name}</span>
                                      {att.size && <span className="opacity-60">({formatFileSize(att.size)})</span>}
                                      <Download className="w-3 h-3 ml-auto" />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-1 gap-2">
                              <p className={`text-xs ${msg.sender_type === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                {new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - {new Date(msg.created_at).toLocaleDateString('es-ES')}
                              </p>
                              {!isSolution && selectedTicket.status !== 'closed' && msg.sender_type !== 'user' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs gap-1 text-muted-foreground hover:text-green-500"
                                  onClick={() => {
                                    markSolution.mutate(
                                      { ticketId: selectedTicket.id, messageId: msg.id },
                                      { onSuccess: () => toast({ title: "Solución marcada", description: "El ticket ha sido marcado como resuelto." }) }
                                    );
                                  }}
                                  disabled={markSolution.isPending}
                                >
                                  <ShieldCheck className="w-3 h-3" />
                                  Marcar como solución
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {selectedTicket.status === 'resolved' ? (
                <div className="p-4 border-t border-border">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-success/10 border border-success/30">
                    <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                    <p className="text-sm text-success font-medium flex-1">Tu ticket fue resuelto por el equipo de soporte.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateStatus.mutate({ ticketId: selectedTicket.id, status: 'open' })}
                    >
                      Reabrir
                    </Button>
                  </div>
                </div>
              ) : selectedTicket.status !== 'closed' ? (
                <div className="p-4 border-t border-border space-y-3">
                  {messageAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {messageAttachments.map(a => (
                        <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border">
                          {a.type.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
                          <span className="text-xs font-medium truncate max-w-[100px]">{a.name}</span>
                          <button onClick={() => removeAttachment(a.id, setMessageAttachments)} className="p-1 hover:bg-destructive/20 rounded-full transition-colors">
                            <X className="w-3 h-3 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input ref={messageFileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => handleFileSelect(e, setMessageAttachments)} />
                    <Button variant="outline" size="icon" onClick={() => messageFileInputRef.current?.click()} className="shrink-0">
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Input 
                      placeholder="Escribe tu mensaje..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    />
                    <Button onClick={handleSendMessage} disabled={isSending || (!newMessage.trim() && messageAttachments.length === 0)}>
                      {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          ) : (
            <Card className="bg-card/50 border-border h-full flex items-center justify-center min-h-[400px]">
              <CardContent className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Selecciona un ticket para ver los detalles</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
